import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { DocumentType } from "domain/entities/document-type";
import { EMAIL } from "domain/enum/email";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { useCreateDocumentMutation } from "presentation/hooks/use-query-document";
import { useDocumentTypeByFilterAll } from "presentation/hooks/use-query-document-type";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";

interface DocumentUploadDialogProps {
  visible: boolean;
  onHide: () => void;
  onUploadSuccess: () => void;
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  visible,
  onHide,
  onUploadSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const { employee } = useEmployeeStore();
  const { email: userEmployeeEmail } = useAuthStore();
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);

  const { data: docTypesData } = useDocumentTypeByFilterAll({
    limit: 100,
    sortBy: "name",
  } as DocumentTypeInputFilter);

  const { mutate: createDocument } = useCreateDocumentMutation();

  const handleUpload = async (event: FileUploadHandlerEvent) => {
    const file = event.files[0];
    if (!file || !employee?.id || !selectedDocType) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Selecione um tipo de documento antes de fazer o upload.",
        life: 3000,
      });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // 1. Upload do ficheiro para o armazenamento
      const folder = `employees/${employee.id}/${selectedDocType.name}`;
      const uploadResponse = await httpClient.post(
        `/assets/${folder}`,
        formData
      );

      const filePath = uploadResponse.data.filePath;

      // 2. Criação do registo do documento na base de dados
      const newDocument: Document = {
        id: undefined,
        documentType: { id: selectedDocType.id },
        employee: { id: employee.id },
        fullPath: filePath,
        lastModifiedBy: userEmployeeEmail ?? EMAIL,
      };

      createDocument(newDocument, {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Documento enviado com sucesso!",
            life: 3000,
          });
          onUploadSuccess();
          onHide();
        },
        onError: () => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: "Falha ao guardar o registo do documento.",
            life: 3000,
          });
        },
      });
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro de Upload",
        detail: "Ocorreu um erro ao enviar o ficheiro.",
        life: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Novo Documento"
        visible={visible}
        style={{ width: "30vw" }}
        onHide={onHide}
        modal
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label htmlFor="docType">Tipo de Documento</label>
            <Dropdown
              id="docType"
              value={selectedDocType}
              options={docTypesData?.data || []}
              onChange={(e) => setSelectedDocType(e.value)}
              optionLabel="name"
              placeholder="Selecione um tipo"
            />
          </div>
          <div className="field col-12">
            <FileUpload
              name="document"
              customUpload
              uploadHandler={handleUpload}
              mode="basic"
              accept="image/*,.pdf"
              chooseLabel="Selecionar Ficheiro"
              disabled={!selectedDocType || isUploading}
              auto
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default DocumentUploadDialog;
